import API from "serviceshift-ui/api-client";

interface JobAssetParams {
  jobId: number;
}

interface DeleteJobAssetParams extends JobAssetParams {
  id: number;
}

interface UploadJobDocumentParams extends JobAssetParams {
  data: FormData;
}

interface UpdateAssetVisibilityParams extends JobAssetParams {
  customer_visible?: boolean;
}

interface UpdateDocumentVisibilityParams extends UpdateAssetVisibilityParams {
  docId: number;
}

interface UpdatePhotoVisibilityParams extends UpdateAssetVisibilityParams {
  photoId: number;
}

type MediaAssetType = "photo" | "video" | "document";

interface BulkUpdateVisibilityParams extends JobAssetParams {
  type: MediaAssetType;
  ids: number[];
  customer_visible: boolean;
}

interface UpdateVideoVisibilityParams extends UpdateAssetVisibilityParams {
  videoId: number;
}

interface InitiateMultipartUploadParams {
  jobId: number;
  extension: string;
  contentType: string;
}

interface InitiateMultipartUploadResponse {
  key: string;
  upload_id: string;
}

interface GetPartUrlsParams {
  key: string;
  uploadId: string;
  partNumbers: number[];
}

interface UploadPart {
  part_number: number;
  url: string;
}

interface CompletedPart {
  part_number: number;
  etag: string;
}

interface CompleteMultipartUploadParams {
  jobId: number;
  key: string;
  uploadId: string;
  parts: CompletedPart[];
  source?: string;
  filename?: string;
}

interface AbortMultipartUploadParams {
  key: string;
  uploadId: string;
}

interface ReservePhotoUploadsParams {
  url: string;
  method: string;
  extensions: string[];
}

interface CompletePhotoUploadParams {
  url: string;
  source: string;
  filename: string;
}

interface ReserveDocumentUploadsParams {
  url: string;
  extensions: string[];
}

interface CompleteDocumentUploadParams {
  url: string;
  filename: string;
}

export default {
  deleteJobPhoto(_: unknown, params: DeleteJobAssetParams) {
    const { jobId, id } = params;
    return API.job.deletePhoto(jobId, id);
  },
  deleteJobDocument(_: unknown, params: DeleteJobAssetParams) {
    const { jobId, id } = params;
    return API.job.deleteDocument(jobId, id);
  },
  uploadJobDocument(_: unknown, params: UploadJobDocumentParams) {
    const { jobId, data } = params;
    return API.job.uploadDocument(jobId, { body: data });
  },
  updateJobDocumentVisibility(
    _: unknown,
    params: UpdateDocumentVisibilityParams
  ) {
    const { jobId, docId, ...visibility } = params;
    return API.job.updateDocumentVisibility(jobId, docId, visibility);
  },
  updateJobPhotoVisibility(_: unknown, params: UpdatePhotoVisibilityParams) {
    const { jobId, photoId, ...visibility } = params;
    return API.job.updatePhotoVisibility(jobId, photoId, visibility);
  },
  bulkUpdateAssetVisibility(_: unknown, params: BulkUpdateVisibilityParams) {
    const { jobId, type, ids, customer_visible } = params;
    const data = { ids, customer_visible };
    const bulkUpdaters = {
      photo: API.job.bulkUpdatePhotoVisibility,
      video: API.job.bulkUpdateVideoVisibility,
      document: API.job.bulkUpdateDocumentVisibility
    };
    return bulkUpdaters[type](jobId, data);
  },
  deleteJobVideo(_: unknown, params: DeleteJobAssetParams) {
    const { jobId, id } = params;
    return API.job.deleteVideo(jobId, id);
  },
  updateJobVideoVisibility(_: unknown, params: UpdateVideoVisibilityParams) {
    const { jobId, videoId, ...visibility } = params;
    return API.job.updateVideoVisibility(jobId, videoId, visibility);
  },

  // Multipart upload actions for large video files
  initiateMultipartUpload(
    _: unknown,
    params: InitiateMultipartUploadParams
  ): Promise<InitiateMultipartUploadResponse> {
    const { jobId, extension, contentType } = params;
    return API.makeRequest(`/v2/jobs/${jobId}/videos/initiate_multipart`, {
      method: "POST",
      data: { extension, content_type: contentType }
    }).then((res) => res.data);
  },

  getUploadPartUrls(
    _: unknown,
    params: GetPartUrlsParams
  ): Promise<{ parts: UploadPart[] }> {
    const { key, uploadId, partNumbers } = params;
    return API.makeRequest("/v2/videos/part_urls", {
      method: "POST",
      data: { key, upload_id: uploadId, part_numbers: partNumbers }
    }).then((res) => res.data);
  },

  completeMultipartUpload(_: unknown, params: CompleteMultipartUploadParams) {
    const { jobId, key, uploadId, parts, source, filename } = params;
    return API.makeRequest(`/v2/jobs/${jobId}/videos/complete_multipart`, {
      method: "POST",
      data: { key, upload_id: uploadId, parts, source, filename }
    }).then((res) => res.data);
  },

  abortMultipartUpload(_: unknown, params: AbortMultipartUploadParams) {
    const { key, uploadId } = params;
    return API.makeRequest("/v2/videos/abort_upload", {
      method: "POST",
      data: { key, upload_id: uploadId }
    });
  },

  // Photo upload actions
  reservePhotoUploads(_: unknown, params: ReservePhotoUploadsParams) {
    const { url, method, extensions } = params;
    return API.makeRequest(url, {
      method,
      data: { extensions }
    }).then((res) => res.data);
  },

  completePhotoUpload(_: unknown, params: CompletePhotoUploadParams) {
    const { url, source, filename } = params;
    return API.makeRequest(url, {
      method: "POST",
      data: { source, filename },
      headers: { ignoreError: true }
    });
  },

  // Document upload actions
  reserveDocumentUploads(_: unknown, params: ReserveDocumentUploadsParams) {
    const { url, extensions } = params;
    return API.makeRequest(url, {
      method: "POST",
      data: { extensions }
    }).then((res) => res.data);
  },

  completeDocumentUpload(_: unknown, params: CompleteDocumentUploadParams) {
    const { url, filename } = params;
    return API.makeRequest(url, {
      method: "POST",
      data: { filename }
    });
  }
};
